import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import Sidebar from "../component/Sidebar";
import Pagination from "./Pagination";
import CollapsibleHeader from "../component/CollapsibleHeader";
import { FaArrowLeft } from "react-icons/fa";

const formatToDDMMYYYY = (dateStr) => {
  if (!dateStr) return "--";
  const [y, m, d] = dateStr.split("-");
  return `${d}-${m}-${y}`;
};

const getStatusClass = (status) => {
  const value = String(status || "").toUpperCase();
  if (value === "PRESENT") return "bg-green-100 text-green-700";
  if (value === "ABSENT") return "bg-red-100 text-red-700";
  if (value === "OFF") return "bg-gray-100 text-gray-700";
  if (value.includes("SL")) return "bg-orange-100 text-orange-700";
  if (value.includes("CL")) return "bg-blue-100 text-blue-700";
  return "bg-yellow-100 text-yellow-700";
};

const getStatusLabel = (row) => {
  const value = String(row.status || "").toUpperCase();
  if (value === "PRESENT" && row.isLate) return "Late Entry";
  if (value === "PRESENT") return "Present";
  if (value === "ABSENT") return "Absent";
  if (value === "OFF") return "OFF";
  return row.status || "--";
};

const getDisplayStatusClass = (row) => {
  if (String(row.status || "").toUpperCase() === "PRESENT" && row.isLate) {
    return "bg-orange-100 text-orange-700";
  }
  return getStatusClass(row.status);
};

const isNonWorkingStatus = (status = "") => {
  const value = String(status || "").toUpperCase();
  return value === "OFF" || value === "ABSENT" || value.includes("SL") || value.includes("CL");
};

const getDisplayValue = (row, key) => {
  if (isNonWorkingStatus(row.status)) return "--";
  const value = row[key];
  return !value || value === "NaNh NaNm" ? "--" : value;
};

const EmployeeAttendanceHistory = () => {
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => 
    localStorage.getItem("attnHistory_selectedDate") || new Date().toISOString().slice(0, 10)
  );
  const [dateFrom, setDateFrom] = useState(() => 
    localStorage.getItem("attnHistory_dateFrom") || new Date().toISOString().slice(0, 10)
  );
  const [dateTo, setDateTo] = useState(() => 
    localStorage.getItem("attnHistory_dateTo") || new Date().toISOString().slice(0, 10)
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  useEffect(() => {
    localStorage.setItem("attnHistory_selectedDate", selectedDate);
    localStorage.setItem("attnHistory_dateFrom", dateFrom);
    localStorage.setItem("attnHistory_dateTo", dateTo);
  }, [selectedDate, dateFrom, dateTo]);

  useEffect(() => {
    const fetchAttendanceRows = async () => {
      setLoading(true);
      try {
        const res = await axios.get("/api/attendance/history-records", {
          params: selectedDate ? { date: selectedDate } : {},
        });
        setAttendanceRows(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        setAttendanceRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAttendanceRows();
  }, [selectedDate]);

  const filteredRows = useMemo(() => {
    const searchValue = searchTerm.trim().toUpperCase();

    return attendanceRows.filter((row) => {
      if (!searchValue) return true;

      const name = String(row.name || "").toUpperCase();
      const employeeId = String(row.employeeId || "").toUpperCase();
      const userId = String(row.userId || "").toUpperCase();
      const department = String(row.department || "").toUpperCase();
      const designation = String(row.designation || "").toUpperCase();

      return (
        name.includes(searchValue) ||
        employeeId.includes(searchValue) ||
        userId.includes(searchValue) ||
        department.includes(searchValue) ||
        designation.includes(searchValue)
      );
    });
  }, [attendanceRows, searchTerm]);

  const startIndex = perPage === "all" ? 0 : (currentPage - 1) * perPage;
  const paginatedRows =
    perPage === "all"
      ? filteredRows
      : filteredRows.slice(startIndex, startIndex + perPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedDate, perPage]);

  useEffect(() => {
    setSelectedRowIds([]);
  }, [selectedDate, searchTerm]);

  const uniqueFilteredUserIds = [...new Set(filteredRows.map((row) => row.userId).filter(Boolean))];
  const allVisibleSelected =
    uniqueFilteredUserIds.length > 0 &&
    uniqueFilteredUserIds.every((userId) => selectedRowIds.includes(userId));

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedRowIds(uniqueFilteredUserIds);
      return;
    }
    setSelectedRowIds([]);
  };

  const handleSelectRow = (userId) => {
    if (!userId) return;
    setSelectedRowIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const handleExportExcel = async () => {
    if (!dateFrom || !dateTo) {
      toast.error("Select from date and to date for export");
      return;
    }

    if (dateFrom > dateTo) {
      toast.error("From date cannot be after to date");
      return;
    }

    if (selectedRowIds.length === 0) {
      toast.error("Select employee rows or use select all before export");
      return;
    }

    try {
      const response = await axios.get("/api/attendance/history-records", {
        params: { fromDate: dateFrom, toDate: dateTo },
      });

      const exportRows = (Array.isArray(response.data) ? response.data : [])
        .filter((row) => selectedRowIds.includes(row.userId))
        .filter((row) => {
          const searchValue = searchTerm.trim().toUpperCase();
          if (!searchValue) return true;

          const values = [
            row.name,
            row.employeeId,
            row.userId,
            row.department,
            row.designation,
          ]
            .map((value) => String(value || "").toUpperCase());

          return values.some((value) => value.includes(searchValue));
        })
        .sort((a, b) => {
          const dateDiff = new Date(a.date) - new Date(b.date);
          if (dateDiff !== 0) return dateDiff;
          return String(a.employeeId).localeCompare(String(b.employeeId), undefined, { numeric: true, sensitivity: "base" });
        });

      if (exportRows.length === 0) {
        toast.error("No attendance records found in this date range");
        return;
      }

      const exportData = exportRows.map((row) => ({
        Date: formatToDDMMYYYY(row.date),
        "Employee ID": row.employeeId || "--",
        Name: row.name || "--",
        Shift: row.shift || "--",
        "Shift Start": row.shiftStart || "--",
        "Shift End": row.shiftEnd || "--",
        "Punch In": getDisplayValue(row, "punchIn"),
        "Punch Out": getDisplayValue(row, "punchOut"),
        Status: getStatusLabel(row),
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance");
      XLSX.writeFile(workbook, `Attendance_History_${dateFrom || "All"}_${dateTo || "All"}.xlsx`);
      toast.success("Attendance exported successfully");
    } catch (error) {
      toast.error("Failed to export attendance");
    }
  };

  return (
    <div className="flex h-screen flex-col overflow-hidden md:flex-row">
      <Sidebar />

      <div className="page-panel">
        <div className="page-panel rounded-md bg-white p-3 shadow-md">
          <div className="page-sticky-header mb-3 bg-white pb-2">
            <div className="page-title-bar mb-3 flex items-center justify-between">
              <h2 className="text-xl font-bold text-dorika-blue">Attendance History</h2>
              <button
                type="button"
                onClick={() => window.history.back()}
                className="inline-flex h-8 w-[110px] items-center justify-center gap-2 rounded-lg bg-blue-700 px-4 text-sm font-semibold text-white shadow transition hover:bg-blue-800"
              >
                <FaArrowLeft />
                <span>Back</span>
              </button>
            </div>

            <CollapsibleHeader title="Attendance History Filters" contentClassName="mt-3">
              <div className="page-title-bar">
                <div className="flex flex-wrap items-end gap-3 xl:flex-nowrap">
                  <div className="field-stack-mobile min-w-[220px] flex-1">
                    <label>Search</label>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value.toUpperCase())}
                      placeholder="Search Name / Emp ID / User ID"
                      className="h-8 rounded border border-dorika-blue px-3 py-1 text-sm focus:outline-none"
                    />
                  </div>

                 {/* Current Date Field */}
                <div className="field-stack-mobile min-w-[150px]">
                  <label>Current Date</label>
                  <input type="date" id="currPicker" className="hidden" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
                  <input type="text" readOnly value={formatToDDMMYYYY(selectedDate)} onClick={() => document.getElementById('currPicker').showPicker()} className="h-8 w-full rounded border border-dorika-blue bg-white px-3 py-1 text-sm focus:outline-none cursor-pointer" />
                </div>

                {/* From Date Field */}
                <div className="field-stack-mobile min-w-[150px]">
                  <label>From Date</label>
                  <input type="date" id="fromPicker" className="hidden" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                  <input type="text" readOnly value={formatToDDMMYYYY(dateFrom)} onClick={() => document.getElementById('fromPicker').showPicker()} className="h-8 w-full rounded border border-dorika-blue bg-white px-3 py-1 text-sm focus:outline-none cursor-pointer" />
                </div>

                {/* To Date Field */}
                <div className="field-stack-mobile min-w-[150px]">
                  <label>To Date</label>
                  <input type="date" id="toPicker" className="hidden" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                  <input type="text" readOnly value={formatToDDMMYYYY(dateTo)} onClick={() => document.getElementById('toPicker').showPicker()} className="h-8 w-full rounded border border-dorika-blue bg-white px-3 py-1 text-sm focus:outline-none cursor-pointer" />
                </div>

                  <div className="field-stack-mobile min-w-[100px]">
                    <label>Show</label>
                    <select
                      value={perPage}
                      onChange={(e) => {
                        const value = e.target.value;
                        setPerPage(value === "all" ? "all" : Number(value));
                      }}
                      className="h-8 rounded border border-dorika-blue bg-white px-3 py-1 text-sm font-semibold text-dorika-blue"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value="all">ALL</option>
                    </select>
                  </div>

                  <div className="field-stack-mobile min-w-[100px]">
                    <label>Records</label>
                    <div className="flex h-8 items-center rounded border border-dorika-blue bg-white px-3 text-sm font-semibold text-dorika-blue">
                      {filteredRows.length}
                    </div>
                  </div>

                  <div className="field-stack-mobile min-w-[140px]">
                    <label>&nbsp;</label>
                    <button
                      type="button"
                      onClick={handleExportExcel}
                      className="h-8 rounded bg-green-600 px-4 text-xs font-bold uppercase text-white shadow-md hover:bg-green-700"
                    >
                      Export Excel
                    </button>
                  </div>
                </div>
              </div>
            </CollapsibleHeader>
          </div>

          <div className="table-scroll-area border shadow">
            <table className="table-fixed-header w-full border-collapse text-xs sm:text-sm">
              <thead className="bg-dorika-blue text-white">
                <tr>
                  <th className="border border-dorika-blue px-2 py-2">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th className="border border-dorika-blue px-2 py-2">SL</th>
                  <th className="border border-dorika-blue px-2 py-2">Date</th>
                  <th className="border border-dorika-blue px-2 py-2">Employee ID</th>
                  {/* <th className="border border-dorika-blue px-2 py-2">User ID</th> */}
                  <th className="border border-dorika-blue px-2 py-2">Name</th>
                  <th className="border border-dorika-blue px-2 py-2">Department</th>
                  <th className="border border-dorika-blue px-2 py-2">Designation</th>
                  <th className="border border-dorika-blue px-2 py-2">Shift</th>
                  <th className="border border-dorika-blue px-2 py-2">Shift Start</th>
                  <th className="border border-dorika-blue px-2 py-2">Shift End</th>
                  <th className="border border-dorika-blue px-2 py-2">Punch In</th>
                  <th className="border border-dorika-blue px-2 py-2">Punch Out</th>
                  <th className="border border-dorika-blue px-2 py-2">Work Time</th>
                  <th className="border border-dorika-blue px-2 py-2">Actual Work</th>
                  <th className="border border-dorika-blue px-2 py-2">Status</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="15" className="border px-3 py-10 text-center text-gray-500">
                      Loading attendance records...
                    </td>
                  </tr>
                ) : paginatedRows.length > 0 ? (
                  paginatedRows.map((row, index) => (
                    <tr key={`${row.userId}-${row.date}-${index}`} className="odd:bg-white even:bg-dorika-blueLight/40">
                      <td className="border border-dorika-blue px-2 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={selectedRowIds.includes(row.userId)}
                          onChange={() => handleSelectRow(row.userId)}
                        />
                      </td>
                      <td className="border border-dorika-blue px-2 py-2 text-center">
                        {perPage === "all" ? index + 1 : startIndex + index + 1}
                      </td>
                      <td className="border border-dorika-blue px-2 py-2 whitespace-nowrap">{formatToDDMMYYYY(row.date)}</td>
                      <td className="border border-dorika-blue px-2 py-2">{row.employeeId || "--"}</td>
                      {/* <td className="border border-dorika-blue px-2 py-2">{row.userId || "--"}</td> */}
                      <td className="border border-dorika-blue px-2 py-2 font-medium">{row.name || "--"}</td>
                      <td className="border border-dorika-blue px-2 py-2">{row.department || "--"}</td>
                      <td className="border border-dorika-blue px-2 py-2">{row.designation || "--"}</td>
                      <td className="border border-dorika-blue px-2 py-2 text-center font-semibold text-indigo-700">{row.shift || "--"}</td>
                      <td className="border border-dorika-blue px-2 py-2 text-center">{row.shiftStart || "--"}</td>
                      <td className="border border-dorika-blue px-2 py-2 text-center">{row.shiftEnd || "--"}</td>
                      <td className="border border-dorika-blue px-2 py-2 text-center font-semibold text-green-700">{getDisplayValue(row, "punchIn")}</td>
                      <td className="border border-dorika-blue px-2 py-2 text-center font-semibold text-orange-700">{getDisplayValue(row, "punchOut")}</td>
                      <td className="border border-dorika-blue px-2 py-2 text-center font-semibold text-blue-700">{getDisplayValue(row, "workTime")}</td>
                      <td className="border border-dorika-blue px-2 py-2 text-center font-semibold text-purple-700">{getDisplayValue(row, "actualWork")}</td>
                      <td className="border border-dorika-blue px-2 py-2 text-center">
                        <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${getDisplayStatusClass(row)}`}>
                          {getStatusLabel(row)}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="15" className="border px-3 py-10 text-center text-gray-500">
                      No attendance records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {perPage !== "all" && (
            <Pagination
              total={filteredRows.length}
              perPage={perPage}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeAttendanceHistory;
